import { synchronousOptions } from "../util/enums";
import { generateClient } from "aws-amplify/api";
import { filter, intersection, groupBy, sortBy, transform, uniqBy } from "lodash";
import { transformGraduateFacultyDropdownOptions } from "./transform";
import { 
    ACADEMIC_UNITS, ADVISORS, CERTIFICATE_PLANS, CO_ADVISORS, PERSON, PROPOSED_ADVISORS, JOB_CODES, DEGREES, DROPDOWNS, FACULTY_MEMBERS,
    GRADUATE_FACULTY_REPRESENTATIVES, GRADUATE_FACULTY_SEARCH, INTERDISCIPLINARY_SPECIALIZATIONS, MINORS, SPECIALIZATIONS,
    getAcademicPrograms,
    STUDENT_INFORMATION, FETCH_USER,
    getSearchAcademicTerms
} from "../actions-index.js";
import * as Queries from '../graphql/queries';
import * as Custom from '../graphql/custom';
import { ACTION_STATUS, DEGREE, ERROR_TYPE, GRAD_LEVEL } from "../util/constants";
import { debouncedOptions, dropdownSubtypes } from "../util/enums.js";
import { buildAction } from "../util/functions";

const API = generateClient();

export function getPerson(query) {
    return async (dispatch, getState) => {
        const { person = {} } = getState()
        if(Object.keys(person?.archive?.[query] ?? {}).length) {
            return dispatch(buildAction(PERSON, { status: ACTION_STATUS.SUCCESS, data: person.archive[query] }));
        }
        try {
            console.log("jmm - action - getPerson - query", query)
            dispatch(buildAction(PERSON, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Custom.getPerson, variables: { query }});
            console.log("jmm - action - getPerson - response", response)
            const person = response.data.getPerson;
            if(!person.emplid) {
                dispatch(buildAction(PERSON, { status: ACTION_STATUS.NOT_FOUND }));
            } else {
                dispatch(buildAction(PERSON, { status: ACTION_STATUS.SUCCESS, data: person, query }));
            }
        } catch(error) {
            console.error("Person Error: ", error);
            dispatch(buildAction(PERSON, { status: ACTION_STATUS.ERROR }));
        }
    };
}

export function resetPerson() {
    return (dispatch) => {
        dispatch(buildAction(PERSON, { status: "" }));
    };
}

// generic function to reset state in Redux
export function resetState(type, payload) {
    return (dispatch) => {
        dispatch(buildAction(type, payload));
    };
}

export function getJobCodes() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.facultyRank, 
                status: ACTION_STATUS.LOADING
            }));
            dispatch(buildAction(JOB_CODES, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getJobCodes, variables: {} });
            const jobCodes = (response?.data?.getJobCodes?.jobCodes ?? []);
            if(jobCodes?.length) {
                dispatch(buildAction(DROPDOWNS, { 
                    type: dropdownSubtypes.facultyRank, 
                    data: jobCodes.map(({ jobTitle, jobCode }, index) => ({
                        id: `jobcode-${jobCode}-${index}`,
                        title: jobTitle,
                        value: jobCode
                    }))
                }));
            }
            // const jobCodeMap = {};
                if(JSON.stringify(jobCodes) === '{}') {
                    dispatch(buildAction(JOB_CODES, { status: ACTION_STATUS.NOT_FOUND }));
                    dispatch(buildAction(DROPDOWNS, { 
                        type: dropdownSubtypes.facultyRank, 
                        status: ACTION_STATUS.NOT_FOUND
                    }));
                } else {
                    dispatch(buildAction(JOB_CODES, { status: ACTION_STATUS.SUCCESS, data: jobCodes }));
                }
            // dispatch(buildAction(JOB_CODE_MAP, { status: ACTION_STATUS.SUCCESS, data: jobCodeMap }));
        } catch(error) {
            console.error("Get Job Code Error: ", error);
            dispatch(buildAction(JOB_CODES,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getTerms (options = {}) {
    const { career = "GRAD", index = "", showFromPresent, showHistorical } = options
    const subtype = `${dropdownSubtypes.term}${index}`;
    let variables = { career }
    if(showFromPresent) {
      variables.future = 'Y'
    }
    if(showHistorical) {
      variables.history = 'Y'
    }

    return async dispatch => {
      try {
        const response = await API.graphql({ query: Queries.getTerms, variables });
          const terms = response?.data?.getTerms?.terms
          if(!terms.length) {
            throw new Error('unable to get terms')
          }

          const data = formatTerms({ data: terms, showFromPresent, showHistorical }) 

          dispatch(buildAction(DROPDOWNS, { 
              type: subtype, 
              data
          }));
      } catch (error) {
        dispatch(buildAction(DROPDOWNS, { 
          type: subtype, 
          status: ACTION_STATUS.NOT_FOUND
      }));
      }
    }
}
function formatTerms(options = {}) {
    const { data = [], showFromPresent, showHistorical } = options
    let slicedData = []
    const indexOfPresent = data.findIndex((values) => {
        return values?.termStatus === 'C'
    })

    if(indexOfPresent === -1) {
        let historical = [];
        if(showHistorical) {
            historical = data.filter(term => term.termStatus === 'H')
            historical = historical.slice(-1 * showHistorical)
        }
        let future = data.filter(term => term.termStatus === 'F')
        future = future.slice(0, showFromPresent)
        slicedData = [...historical, ...future]
    } else {
        let start = showHistorical ? (indexOfPresent - showHistorical) : indexOfPresent
        let end = indexOfPresent + showFromPresent
        slicedData = data.slice(start, end)
    }
    return slicedData.map(item => {
        const id = item?.descr.replace(' ', '-');
        const title = item?.descr.replace(' Term', '').replace(' Semester', '');
        return { id, value: item?.strm, title, endDate: item?.termEndDt };
    });
}

export function fetchDebouncedOptions(subtype, state = {}, options = {}) {
    return async (dispatch, getState) => {
        const { query = "", filterState = {}, overrideFaculty, index = "" } = options;
        if(!query || query.length < 2) {
            return await dispatch(buildAction(DROPDOWNS, { 
                type: subtype, 
                status: "MISSING_QUERY",
            }));
        }
        const queries = (state.queries ?? []);
        const dropdownOptions = (state.options ?? []);
        const titles = dropdownOptions.map(dropdownOption => (dropdownOption.selectedTitle ?? dropdownOption.title));
        
        if(options?.filter?.user) { // filtering data by user field
            const state = getState();
            const user = state.authentication.user;
            const fieldName = options.filter.user;
            const fieldValue = user[fieldName];
            filterState[fieldName] = { dataField: fieldName, value: { [fieldName]: fieldValue } };
        }

        if(!queries.includes(query) && !titles.includes(query)) {
            if(subtype === dropdownSubtypes.advisor) {
                return await dispatch(searchGraduateFaculty(query, ADVISORS, dropdownSubtypes.advisor, filterState, overrideFaculty));
            }
            if(subtype === dropdownSubtypes.coAdvisor) {
                return await dispatch(searchGraduateFaculty(query, CO_ADVISORS, dropdownSubtypes.coAdvisor, filterState, overrideFaculty));
            }
            if(subtype === dropdownSubtypes.facultyMember) {
                return await dispatch(searchGraduateFaculty(query, FACULTY_MEMBERS, `${dropdownSubtypes.facultyMember}${index}`, filterState, overrideFaculty));
            }
            if(subtype === dropdownSubtypes.gradFacultyRepresentative) {
                return await dispatch(searchGraduateFaculty(query, GRADUATE_FACULTY_REPRESENTATIVES, dropdownSubtypes.gradFacultyRepresentative, filterState, overrideFaculty));
            }
            if(subtype === dropdownSubtypes.proposedAdvisor) {
                return await dispatch(searchGraduateFaculty(query, PROPOSED_ADVISORS, dropdownSubtypes.proposedAdvisor, filterState, overrideFaculty));
            }
        }
    }
}

export function refreshDropdowns(subtype, options = {}) {
    const { index = "" } = options;
    return async (dispatch, getState) => {
        try {
            const state = getState()
            const fetchData = checkIfFetchingRequired(state, `${subtype}${index}`);
            if(debouncedOptions.includes(subtype)) {
                return await dispatch(fetchDebouncedOptions(subtype, state?.dropdowns?.[subtype], options))
            }
            if(!fetchData) {
                return {}
            }
            if([dropdownSubtypes.graduatePrograms, dropdownSubtypes.proposedGraduatePrograms].includes(subtype)) {
                return await dispatch(getAcademicPrograms())
            }
            if(subtype === dropdownSubtypes.facultyRank) {
                return await dispatch(getJobCodes())
            }
            if(subtype === dropdownSubtypes.term) {
                return await dispatch(getTerms({...options?.filter, index}))
            }
            if(subtype === dropdownSubtypes.degrees) {
                return await dispatch(getDegrees());
            }
            if(subtype === dropdownSubtypes.gfnDegrees) {
                return await dispatch(getGfnDegrees());
            }
            if(subtype === dropdownSubtypes.certicatePlans) {
                return await dispatch(getCertificatePlans());
            }
            if(subtype === dropdownSubtypes.specialization) {
                return await dispatch(getSpecializations());
            }
            if(subtype === dropdownSubtypes.designationMinor) {
                return await dispatch(getMinors());
            }
            if(subtype === dropdownSubtypes.designationGIS) {
                return await dispatch(getInterdisciplinarySpecializations());
            }
            if(subtype === dropdownSubtypes.academicUnit) {
                return await dispatch(getAcademicUnits());
            }
            if(subtype === dropdownSubtypes.gradPlanAdminPrograms) {
                return await dispatch(getGradPlanAdminPrograms());
            }
            if(subtype === dropdownSubtypes.searchTerms) {
                return await dispatch(getSearchAcademicTerms());
            }
        } catch (error) {
            console.error(error)
        }
    }
}

const checkIfFetchingRequired = (state, subtype) => {
    const relatedOptions = state?.dropdowns?.[subtype]?.options

    return !relatedOptions?.length
}

export function getDegrees() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DROPDOWNS, { type: dropdownSubtypes.degrees, status: ACTION_STATUS.LOADING }));
            dispatch(buildAction(DEGREES, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getDegrees, variables: {} });
            let degrees = (response?.data?.getDegrees?.degrees ?? []);
            degrees = sortBy(degrees, ["gradLevel"]);
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.degrees,
                status: ACTION_STATUS.SUCCESS,
                data: degrees.map(({ degree, gradLevel }, index) => ({
                    id: `degree-${degree}-${index}`,
                    group: gradLevel,
                    selectedTitle: `${gradLevel} - ${degree}`,
                    title: degree,
                    value: degree
                }))
            }));
            dispatch(buildAction(DEGREES, { status: ACTION_STATUS.SUCCESS, data: degrees }));
        } catch(error) {
            console.error("Get Degrees Error: ", error);
            dispatch(buildAction(DEGREES,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getGfnDegrees() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DROPDOWNS, { type: dropdownSubtypes.gfnDegrees, status: ACTION_STATUS.LOADING }));
            
            // Use the static data from synchronousOptions but format it like getDegrees
            const gfnDegreesData = synchronousOptions[dropdownSubtypes.gfnDegrees];
            
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.gfnDegrees,
                status: ACTION_STATUS.SUCCESS,
                data: gfnDegreesData
            }));
        } catch(error) {
            console.error("Get GFN Degrees Error: ", error);
            dispatch(buildAction(DROPDOWNS, { type: dropdownSubtypes.gfnDegrees, status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getCertificatePlans() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DROPDOWNS, { type: dropdownSubtypes.certicatePlans, status: ACTION_STATUS.LOADING }));
            dispatch(buildAction(CERTIFICATE_PLANS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getAcademicPlans, variables: { filter: { degree: DEGREE.CERTIFICATE, status: "A" } } });
            const certificatePlans = (response?.data?.getAcademicPlans?.academicPlans ?? []);
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.certicatePlans,
                status: ACTION_STATUS.SUCCESS,
                data: sortBy(certificatePlans.map(({ academicPlan, descr }, index) => ({
                    id: `certificatePlan-${academicPlan}-${index}`,
                    title: `${academicPlan} - ${descr}`,
                    value: academicPlan
                })), ["title"])
            }));
            dispatch(buildAction(CERTIFICATE_PLANS, { status: ACTION_STATUS.SUCCESS, data: certificatePlans }));
        } catch(error) {
            console.error("Get Certificate Plans Error: ", error);
            dispatch(buildAction(CERTIFICATE_PLANS,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getSpecializations() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DROPDOWNS, { type: dropdownSubtypes.specialization, status: ACTION_STATUS.LOADING }));
            dispatch(buildAction(SPECIALIZATIONS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getAcademicPlans, variables: { filter: { degree: DEGREE.SPECIALIZATION, status: "A" } } });
            const specializations = (response?.data?.getAcademicPlans?.academicPlans ?? []);
            // const specializations = (response?.data?.getAcademicPlans?.graduateProgram ?? []);
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.specialization,
                status: ACTION_STATUS.SUCCESS,
                data: sortBy(specializations.map(({ academicPlan, graduateProgram, acadSubPlan, descr }, index) => ({
                    id: `specialization-${acadSubPlan}-${index}`,
                    title: `${acadSubPlan} - ${descr}`,
                    value: acadSubPlan,
                    academicPlan // used for filtering
                })), ["title"])
            }));
            dispatch(buildAction(SPECIALIZATIONS, { status: ACTION_STATUS.SUCCESS, data: specializations }));
        } catch(error) {
            console.error("Get Specializations Error: ", error);
            dispatch(buildAction(SPECIALIZATIONS,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getMinors() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DROPDOWNS, { type: dropdownSubtypes.designationMinor, status: ACTION_STATUS.LOADING }));
            dispatch(buildAction(MINORS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getAcademicPlans, variables: { filter: { degree: DEGREE.MINOR, status: "A" } } });
            const minors = (response?.data?.getAcademicPlans?.academicPlans ?? []);
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.designationMinor,
                status: ACTION_STATUS.SUCCESS, 
                data: sortBy(minors.map(({ academicPlan, descr }, index) => ({
                    id: `minor-${academicPlan}-${index}`,
                    title: `${academicPlan} - ${descr}`,
                    value: academicPlan,
                })), ["title"])
            }));
            dispatch(buildAction(MINORS, { status: ACTION_STATUS.SUCCESS, data: minors }));
        } catch(error) {
            console.error("Get Minors Error: ", error);
            dispatch(buildAction(MINORS,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getInterdisciplinarySpecializations() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DROPDOWNS, { type: dropdownSubtypes.designationGIS, status: ACTION_STATUS.LOADING }));
            dispatch(buildAction(INTERDISCIPLINARY_SPECIALIZATIONS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getAcademicPlans, variables: { filter: { degree: DEGREE.INTERDISCIPLINARY_SPECIALIZATION, status: "A" } } });
            const interdisciplinarySpecializations = (response?.data?.getAcademicPlans?.academicPlans ?? []);
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.designationGIS,
                status: ACTION_STATUS.SUCCESS,
                data: sortBy(interdisciplinarySpecializations.map(({ academicPlan, descr }, index) => ({
                    id: `interdisciplinary-specialization-${academicPlan}-${index}`,
                    title: `${academicPlan} - ${descr}`,
                    value: academicPlan,
                })), ["title"])
            }));
            dispatch(buildAction(INTERDISCIPLINARY_SPECIALIZATIONS, { status: ACTION_STATUS.SUCCESS, data: interdisciplinarySpecializations }));
        } catch(error) {
            console.error("Get Interdisciplinary Specializations Error: ", error);
            dispatch(buildAction(INTERDISCIPLINARY_SPECIALIZATIONS, { status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getAcademicUnits() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DROPDOWNS, { type: dropdownSubtypes.academicUnit, status: ACTION_STATUS.LOADING }));
            dispatch(buildAction(ACADEMIC_UNITS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getAcademicPlans, variables: { filter: { status: "A" } } });
            let academicUnits = (response?.data?.getAcademicPlans?.academicPlans ?? []);
            academicUnits = academicUnits.filter(academicUnit => {
                return (
                        intersection([GRAD_LEVEL.DMA, GRAD_LEVEL.DOCTORAL, GRAD_LEVEL.EDUCATIONAL_SPECIALIST, GRAD_LEVEL.MASTER, GRAD_LEVEL.MASTERCB, GRAD_LEVEL.PROFESSIONAL], academicUnit.gradLevel).length > 0 &&
                    academicUnit.acadSubPlan === null
                );
            });
            academicUnits = uniqBy(academicUnits, "graduateProgram");
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.academicUnit,
                status: ACTION_STATUS.SUCCESS, 
                data: sortBy(academicUnits.map(({ graduateProgram, descr }, index) => ({
                    id: `academic-unit-${graduateProgram}-${index}`,
                    title: `${graduateProgram} - ${descr}`,
                    value: graduateProgram,
                })), ["title"])
            }));
            dispatch(buildAction(ACADEMIC_UNITS, { status: ACTION_STATUS.SUCCESS, data: academicUnits }));
        } catch(error) {
            console.error("Get Academic Units Error: ", error);
            dispatch(buildAction(ACADEMIC_UNITS,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function searchGraduateFaculty(query, action = GRADUATE_FACULTY_SEARCH, dropdownSubtype = null, filterState, overrideFaculty) {
    return async (dispatch) => {
        try {
            if(dropdownSubtype) dispatch(buildAction(DROPDOWNS, { type: dropdownSubtype, status: ACTION_STATUS.LOADING, query }));
            dispatch(buildAction(action, { status: ACTION_STATUS.LOADING }));
            let graduateFaculty = overrideFaculty

            if(!graduateFaculty && query) {
                const response = await API.graphql({ query: Queries.searchGraduateFaculty, variables: { query } });
                graduateFaculty = (response?.data?.searchGraduateFaculty?.graduateFaculty ?? []);
            }         

            let data = transformGraduateFacultyDropdownOptions(graduateFaculty, !!overrideFaculty?.length);
            if(filterState) {
                const dataFilter = transform(filterState, (result, filterValue) => {
                    result[filterValue.dataField] = filterValue.value[filterValue.dataField];
                }, {});
                data = filter(data, dataFilter);
            }
            if(dropdownSubtype) {
                dispatch(buildAction(DROPDOWNS, {
                    type: dropdownSubtype,
                    status: ACTION_STATUS.SUCCESS,
                    data,
                    query
                }));
            }
            dispatch(buildAction(action, { status: ACTION_STATUS.SUCCESS, data }));
        } catch(error) {
            console.error("Search Graduate Faculty Error: ", error);
            dispatch(buildAction(action,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getStudentInfo(studentid) {
    return async (dispatch) => {
        try {
            dispatch(buildAction(STUDENT_INFORMATION, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Custom.getStudentInfo, variables: { studentid } });
            if(response?.data?.getStudentInfo?.record?.osuid) {
                dispatch(buildAction(STUDENT_INFORMATION, response?.data?.getStudentInfo?.record));
            } else {
                throw new Error(`Unexpected response: ${response}`)
            }
        } catch (error) {
            console.error(error)
            dispatch(buildAction(STUDENT_INFORMATION, { status: ACTION_STATUS.ERROR }));
        }
    }
}

export function getGradPlanAdminPrograms() {
    return async (dispatch, getState) => {
        const { authentication = {} } = getState();
        const { user = {} } = authentication;
        const { gradPlanAdminPrograms = [], isAdmin = false, isGradPlanAdmin = false } = user;
        try {
            dispatch(buildAction(DROPDOWNS, { type: dropdownSubtypes.academicUnit, status: ACTION_STATUS.LOADING }));
            dispatch(buildAction(ACADEMIC_UNITS, { status: ACTION_STATUS.LOADING }));
            
            const getAcademicUnit = (academicPlans) => {
                let academicUnits = (academicPlans ?? []);
                academicUnits = academicUnits.filter(academicUnit => {
                    return (
                        intersection([GRAD_LEVEL.DMA, GRAD_LEVEL.DOCTORAL, GRAD_LEVEL.EDUCATIONAL_SPECIALIST, GRAD_LEVEL.MASTER, GRAD_LEVEL.MASTERCB, GRAD_LEVEL.PROFESSIONAL,GRAD_LEVEL.INTERDISC_SPEC], academicUnit.gradLevel).length > 0 &&
                        academicUnit.acadSubPlan === null
                    );
                });
                academicUnits = uniqBy(academicUnits, "graduateProgram");
                return (academicUnits.length > 0 ? academicUnits[0] : null);
            };

            let academicUnits = [];
            if(isAdmin === true) {
                const response = await API.graphql({ query: Queries.getAcademicPlans, variables: { filter: { status: "A" } } });
                const academicPlans = (response?.data?.getAcademicPlans?.academicPlans ?? []);
                const academicPlansByProgram = groupBy(academicPlans, "graduateProgram");
                for(const program of Object.keys(academicPlansByProgram)) {
                    const academicPlans = academicPlansByProgram[program];
                    const academicUnit = getAcademicUnit(academicPlans);
                    if(academicUnit) academicUnits.push(academicUnit);
                }
            } else if(isGradPlanAdmin === true) {
                for(const program of gradPlanAdminPrograms) {
                    const response = await API.graphql({ query: Queries.getAcademicPlans, variables: { filter: { program, status: "A" } } });
                    const academicUnit = getAcademicUnit(response?.data?.getAcademicPlans?.academicPlans);
                    if(academicUnit) academicUnits.push(academicUnit);
                }
            }

            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.gradPlanAdminPrograms,
                status: ACTION_STATUS.SUCCESS, 
                data: sortBy(academicUnits.map(({ graduateProgram, descr }, index) => ({
                    id: `academic-unit-${graduateProgram}-${index}`,
                    title: `${graduateProgram} - ${descr}`,
                    value: graduateProgram,
                })), ["title"])
            }));
            dispatch(buildAction(ACADEMIC_UNITS, { status: ACTION_STATUS.SUCCESS, data: academicUnits }));
        } catch(error) {
            console.error("Get Grad Plan Admin Programs Error: ", error);
            dispatch(buildAction(ACADEMIC_UNITS,{ status: ACTION_STATUS.ERROR }));
        }
    };
}
